import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@f2e/ui/components/base"

import {
  FetchStatus,
  organizationInvitationBatchConfirmResultResponseToResult,
  organizationInvitationBatchPreviewResponseToPreview,
  organizationInvitationCapacityResponseToCapacity,
  useBatchPreviewOrganizationInvitationApi,
  useConfirmOrganizationInvitationBatchApi,
  useGetOrganizationInvitationCapacityApi,
} from "@/hooks/api"
import { useToast } from "@/hooks"
import { useStore } from "@/store"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"
import { Member } from "@/types"

export interface MemberDialogProps {
  currentMembers: Member[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

const INVITATION_PREVIEW_LANGUAGES = ["en", "zh-TW", "ja"] as const

const normalizeInvitationPreviewLanguage = (language?: string) => {
  const normalizedLanguage = (language || "").toLowerCase()
  if (normalizedLanguage === "zh-tw" || normalizedLanguage.startsWith("zh")) {
    return "zh-TW"
  }
  if (normalizedLanguage === "ja" || normalizedLanguage.startsWith("ja")) {
    return "ja"
  }
  return "en"
}

interface DraftInvitationItem {
  action: "direct_add" | "email_invitation"
  email: string
  workspaceId: string
  workspaceName: string
  organizationName: string
  existingUserName?: string | null
  existingUserEmail?: string | null
  previews: Record<string, { subject: string; bodyText: string }>
  isResend: boolean
  selectedLanguage: string
}

const draftItemConsumesSeat = (
  item: Pick<DraftInvitationItem, "action" | "isResend">
) => item.action === "direct_add" || !item.isResend

const MemberDialog = ({
  currentMembers,
  open,
  onOpenChange,
  onSuccess,
}: MemberDialogProps) => {
  const { t, i18n } = useTranslation()
  const { alertToast, successToast } = useToast()
  const [currentOrganization, workspaces] = useStore((state) => [
    state.currentOrganization,
    state.workspaces,
  ])
  const defaultPreviewLanguage = normalizeInvitationPreviewLanguage(
    i18n.resolvedLanguage || i18n.language
  )
  const [email, setEmail] = useState("")
  const [workspaceId, setWorkspaceId] = useState("")
  const [formError, setFormError] = useState("")
  const [step, setStep] = useState<"edit" | "preview">("edit")
  const [draftItems, setDraftItems] = useState<DraftInvitationItem[]>([])
  const [capacity, setCapacity] = useState<ReturnType<
    typeof organizationInvitationCapacityResponseToCapacity
  > | null>(null)

  const { status: capacityStatus, fetchFn: fetchCapacity } =
    useGetOrganizationInvitationCapacityApi(currentOrganization?.id || "")
  const { status: addDraftStatus, fetchFn: previewDraftItem } =
    useBatchPreviewOrganizationInvitationApi(currentOrganization?.id || "")
  const { status: confirmStatus, fetchFn: confirmInvitationBatch } =
    useConfirmOrganizationInvitationBatchApi(currentOrganization?.id || "")

  const normalizedEmail = useMemo(() => email.trim().toLowerCase(), [email])

  const resetDialog = () => {
    setEmail("")
    setWorkspaceId("")
    setFormError("")
    setStep("edit")
    setDraftItems([])
    setCapacity(null)
  }

  useEffect(() => {
    if (!open || !currentOrganization) return
    resetDialog()
    void fetchCapacity({
      callbackFn: (resp) => {
        if (!resp?.data) return
        setCapacity(organizationInvitationCapacityResponseToCapacity(resp.data))
      },
    }).catch(() => {
      alertToast({
        title: t("settings.users.invitation.errors.capacity.title"),
        description: t("settings.users.invitation.errors.capacity.description"),
      })
    })
  }, [alertToast, currentOrganization, fetchCapacity, open, t])

  const draftSeatCount = draftItems.filter(draftItemConsumesSeat).length

  const remainingAfterDraft =
    capacity?.remaining === null || capacity?.remaining === undefined
      ? null
      : capacity.remaining - draftSeatCount
  const usedAfterDraft = capacity ? capacity.used + draftSeatCount : 0
  const draftExceedsRemaining =
    remainingAfterDraft !== null ? remainingAfterDraft < 0 : false

  const validateDraftInput = () => {
    if (!workspaceId) {
      setFormError(t("settings.users.invitation.errors.workspaceRequired"))
      return false
    }
    if (!normalizedEmail) {
      setFormError(t("settings.users.invitation.errors.emailRequired"))
      return false
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setFormError(t("settings.users.invitation.errors.invalidEmail"))
      return false
    }
    if (
      draftItems.some((item) => item.email.toLowerCase() === normalizedEmail)
    ) {
      setFormError(t("settings.users.invitation.errors.duplicateDraft"))
      return false
    }
    if (
      currentMembers.some(
        (member) => member.email.toLowerCase() === normalizedEmail
      )
    ) {
      setFormError(t("settings.users.invitation.errors.alreadyMember"))
      return false
    }
    setFormError("")
    return true
  }

  const handleAddToDraft = () => {
    if (!currentOrganization || !validateDraftInput()) return
    const selectedWorkspaceId = workspaceId
    if (!selectedWorkspaceId) return

    void previewDraftItem({
      jsonBody: {
        items: [
          {
            email: normalizedEmail,
            workspace_id: selectedWorkspaceId,
          },
        ],
      },
      callbackFn: (resp) => {
        if (!resp?.data) return
        const previewBatch =
          organizationInvitationBatchPreviewResponseToPreview(resp.data)
        const previewItem = previewBatch.items[0]
        if (!previewItem) return
        const nextDraftSeatCount =
          draftSeatCount + (draftItemConsumesSeat(previewItem) ? 1 : 0)

        if (
          previewBatch.inviteCapacity.remaining !== null &&
          nextDraftSeatCount > previewBatch.inviteCapacity.remaining
        ) {
          setCapacity(previewBatch.inviteCapacity)
          setFormError(t("settings.users.invitation.capacity.atLimit"))
          return
        }

        setDraftItems((prev) => [
          ...prev,
          {
            ...previewItem,
            selectedLanguage: defaultPreviewLanguage,
          },
        ])
        setCapacity(previewBatch.inviteCapacity)
        setEmail("")
        setFormError("")
      },
    }).catch((error) => {
      const message = getUserFacingApiErrorMessage(
        error,
        t,
        t("settings.users.invitation.errors.preview.description")
      )
      setFormError(message)
    })
  }

  const handleRemoveDraftItem = (emailToRemove: string) => {
    setDraftItems((prev) => prev.filter((item) => item.email !== emailToRemove))
    setFormError("")
  }

  const handleConfirm = () => {
    if (
      !currentOrganization ||
      draftItems.length === 0 ||
      draftExceedsRemaining
    )
      return

    void confirmInvitationBatch({
      jsonBody: {
        items: draftItems.map((item) => ({
          email: item.email,
          workspace_id: item.workspaceId,
          language: item.selectedLanguage,
        })),
      },
      callbackFn: (resp) => {
        if (!resp?.data) return
        const result = organizationInvitationBatchConfirmResultResponseToResult(
          resp.data
        )
        successToast({
          title: t("settings.users.invitation.success.batch.title"),
          description: t(
            "settings.users.invitation.success.batch.description",
            {
              directAddCount: result.directAddCount,
              invitationEmailCount: result.invitationEmailCount,
            }
          ),
        })
        onSuccess()
        onOpenChange(false)
      },
    }).catch((error) => {
      const message = getUserFacingApiErrorMessage(
        error,
        t,
        t("settings.users.invitation.errors.create.description")
      )
      setStep("edit")
      setFormError(message)
      alertToast({
        title: t("settings.users.invitation.errors.create.title"),
        description: message,
      })
    })
  }

  const updateDraftLanguage = (emailToUpdate: string, language: string) => {
    setDraftItems((prev) =>
      prev.map((item) =>
        item.email === emailToUpdate
          ? { ...item, selectedLanguage: language }
          : item
      )
    )
  }

  return (
    <Dialog
      modal={false}
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen)
        if (!nextOpen) {
          resetDialog()
        }
      }}
    >
      <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-[760px]">
        <DialogHeader>
          <DialogTitle>{t("settings.users.invitation.title")}</DialogTitle>
          <DialogDescription>
            {step === "edit"
              ? t("settings.users.invitation.description")
              : t("settings.users.invitation.preview.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1">
          <div className="space-y-4">
            {capacity && capacity.limit !== null && (
              <div
                className={`rounded-md border px-3 py-2 text-sm ${
                  draftExceedsRemaining || capacity.atLimit
                    ? "border-red-200 bg-red-50 text-red-700"
                    : "border-slate-200 bg-slate-50 text-slate-700"
                }`}
              >
                {draftExceedsRemaining || capacity.atLimit
                  ? t("settings.users.invitation.capacity.atLimit")
                  : t("settings.users.invitation.capacity.remaining", {
                      remaining: remainingAfterDraft ?? 0,
                      limit: capacity.limit,
                      used: usedAfterDraft,
                    })}
              </div>
            )}

            {step === "edit" && (
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-[1fr_1fr_auto]">
                  <div className="space-y-2">
                    <Label>
                      {t("settings.users.invitation.workspaceLabel")}
                    </Label>
                    <Select value={workspaceId} onValueChange={setWorkspaceId}>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t(
                            "settings.users.invitation.workspacePlaceholder"
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {workspaces.map((workspace) => (
                            <SelectItem key={workspace.id} value={workspace.id}>
                              {workspace.name}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="invitation-email">
                      {t("settings.users.invitation.emailLabel")}
                    </Label>
                    <Input
                      id="invitation-email"
                      type="email"
                      placeholder={t(
                        "settings.users.invitation.emailPlaceholder"
                      )}
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                    />
                  </div>

                  <div className="flex items-end">
                    <Button
                      type="button"
                      onClick={handleAddToDraft}
                      disabled={
                        addDraftStatus === FetchStatus.Loading ||
                        capacityStatus === FetchStatus.Loading
                      }
                    >
                      {t("settings.users.invitation.addButton")}
                    </Button>
                  </div>
                </div>

                {formError && (
                  <div className="text-sm text-red-600">{formError}</div>
                )}

                <div className="space-y-2">
                  <Label>{t("settings.users.invitation.draftListTitle")}</Label>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>
                          {t("settings.users.invitation.preview.email")}
                        </TableHead>
                        <TableHead>
                          {t("settings.users.invitation.preview.workspace")}
                        </TableHead>
                        <TableHead>
                          {t("settings.users.invitation.list.type")}
                        </TableHead>
                        <TableHead>
                          {t("settings.users.invitation.preview.action")}
                        </TableHead>
                        <TableHead className="w-20" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {draftItems.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-slate-500">
                            {t("settings.users.invitation.emptyDraft")}
                          </TableCell>
                        </TableRow>
                      ) : (
                        draftItems.map((item) => (
                          <TableRow key={item.email}>
                            <TableCell>
                              <div className="space-y-1">
                                <div>{item.email}</div>
                                {item.action === "direct_add" &&
                                  item.existingUserName && (
                                    <div className="text-xs text-slate-500">
                                      {item.existingUserName}
                                    </div>
                                  )}
                              </div>
                            </TableCell>
                            <TableCell>{item.workspaceName}</TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  item.action === "direct_add"
                                    ? "secondary"
                                    : "default"
                                }
                              >
                                {item.action === "direct_add"
                                  ? t(
                                      "settings.users.invitation.list.existingUser"
                                    )
                                  : t("settings.users.invitation.list.newUser")}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {item.action === "direct_add"
                                ? t(
                                    "settings.users.invitation.list.willDirectAdd"
                                  )
                                : item.isResend
                                  ? t(
                                      "settings.users.invitation.list.willResend"
                                    )
                                  : t(
                                      "settings.users.invitation.list.willSendInvitation"
                                    )}
                            </TableCell>
                            <TableCell>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  handleRemoveDraftItem(item.email)
                                }
                              >
                                {t("settings.users.invitation.list.remove")}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {step === "preview" && (
              <div className="space-y-4">
                {draftItems.map((item) => {
                  const emailPreview =
                    item.previews[item.selectedLanguage] ||
                    item.previews[defaultPreviewLanguage] ||
                    item.previews[Object.keys(item.previews)[0] || ""]

                  return (
                    <div
                      key={item.email}
                      className="rounded-md border border-slate-200 px-4 py-4"
                    >
                      <div className="mb-3 flex flex-wrap items-center gap-2">
                        <Badge
                          variant={
                            item.action === "direct_add"
                              ? "secondary"
                              : "default"
                          }
                        >
                          {item.action === "direct_add"
                            ? t("settings.users.invitation.list.existingUser")
                            : t("settings.users.invitation.list.newUser")}
                        </Badge>
                        <span className="text-sm font-medium">
                          {item.email}
                        </span>
                      </div>

                      <div className="grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
                        <div>
                          <strong>
                            {t(
                              "settings.users.invitation.preview.organization"
                            )}
                            :
                          </strong>{" "}
                          {item.organizationName}
                        </div>
                        <div>
                          <strong>
                            {t("settings.users.invitation.preview.workspace")}:
                          </strong>{" "}
                          {item.workspaceName}
                        </div>
                        <div className="sm:col-span-2">
                          <strong>
                            {t("settings.users.invitation.preview.action")}:
                          </strong>{" "}
                          {item.action === "direct_add"
                            ? t("settings.users.invitation.preview.directAdd")
                            : item.isResend
                              ? t("settings.users.invitation.preview.resend")
                              : t(
                                  "settings.users.invitation.preview.sendEmail"
                                )}
                        </div>
                      </div>

                      {item.action === "direct_add" ? (
                        <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-700">
                          {t(
                            "settings.users.invitation.preview.directAddDescription",
                            {
                              email: item.existingUserEmail || item.email,
                              name:
                                item.existingUserName ||
                                item.existingUserEmail ||
                                item.email,
                            }
                          )}
                        </div>
                      ) : (
                        <div className="mt-3 space-y-3">
                          <div className="space-y-2">
                            <Label>
                              {t(
                                "settings.users.invitation.preview.languageLabel"
                              )}
                            </Label>
                            <Select
                              value={item.selectedLanguage}
                              onValueChange={(language) =>
                                updateDraftLanguage(item.email, language)
                              }
                            >
                              <SelectTrigger>
                                <SelectValue
                                  placeholder={t(
                                    "settings.users.invitation.preview.languagePlaceholder"
                                  )}
                                />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  {INVITATION_PREVIEW_LANGUAGES.map(
                                    (language) => (
                                      <SelectItem
                                        key={language}
                                        value={language}
                                      >
                                        {t(
                                          `settings.users.invitation.preview.languages.${language}`
                                        )}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label>
                              {t("settings.users.invitation.preview.subject")}
                            </Label>
                            <div className="break-all rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700">
                              {emailPreview?.subject}
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label>
                              {t("settings.users.invitation.preview.body")}
                            </Label>
                            <pre className="max-h-[240px] overflow-x-auto whitespace-pre-wrap break-all rounded-md border border-slate-200 px-3 py-3 text-sm text-slate-700">
                              {emailPreview?.bodyText}
                            </pre>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="border-t pt-4">
          {step === "preview" ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep("edit")}
                disabled={confirmStatus === FetchStatus.Loading}
              >
                {t("common.back")}
              </Button>
              <Button
                type="button"
                onClick={handleConfirm}
                disabled={
                  confirmStatus === FetchStatus.Loading ||
                  draftItems.length === 0 ||
                  draftExceedsRemaining
                }
              >
                {t("common.confirm")}
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={
                  capacityStatus === FetchStatus.Loading ||
                  addDraftStatus === FetchStatus.Loading
                }
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="button"
                onClick={() => setStep("preview")}
                disabled={draftItems.length === 0 || draftExceedsRemaining}
              >
                {t("settings.users.invitation.previewButton")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default MemberDialog
